import React, { memo, useCallback, useMemo, useState } from 'react';
import type { AccordionProps } from 'tsconfig/types';
import { useAnchorScroll } from '../../../hooks/useAnchorScroll';
import Accordion from '../../Accordion/Accordion';

export const CmsAccordion = memo(({ contentItem }: AccordionProps) => {
  const { reference } = contentItem.fields || {};
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handleClick = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Map each author-defined anchor id to its panel index so a matching hash can open it.
  const anchorIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    reference?.forEach((item: any, index: number) => {
      const anchorId = item?.fields?.anchorId;
      if (anchorId) {
        map.set(anchorId, index);
      }
    });
    return map;
  }, [reference]);

  const anchorIds = useMemo(() => Array.from(anchorIndexMap.keys()), [anchorIndexMap]);

  const handleAnchorMatch = useCallback(
    (anchorId: string) => {
      const index = anchorIndexMap.get(anchorId);
      if (index !== undefined) {
        setActiveIndex(index);
      }
    },
    [anchorIndexMap],
  );

  useAnchorScroll(anchorIds, handleAnchorMatch);

  const schema = (data: any) => {
    const schemaListItems = (scheduleItems: any) => {
      const getAnswers = (answer: any) => answer?.content;

      return scheduleItems?.map((hit: any) => ({
        '@type': 'Question',
        name: hit?.fields?.entryTitle,
        acceptedAnswer: {
          '@type': 'Answer',
          text: getAnswers(hit?.fields),
        },
      }));
    };

    return {
      '@context': 'http://schema.org',
      '@type': 'FAQPage',
      mainEntity: schemaListItems(data),
    };
  };

  return (
    <>
      {reference?.map((item: any, index: number) => (
        <Accordion
          key={`item-${item?.sys?.id}`}
          id={`item-${item?.sys?.id}`}
          anchorId={item?.fields?.anchorId}
          entryTitle={item?.fields?.entryTitle}
          content={item?.fields?.content}
          index={index}
          activeIndex={activeIndex}
          handleClick={(indexValue: number) => handleClick(indexValue)}
        />
      ))}

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema(reference)) }} />
    </>
  );
});
